/**
 * PubNub plumbing — Phase 5.6.
 *
 * Subscribes the React shell to the same channels `api/src/services/pubnub.service.ts`
 * already publishes to (`servis`, `uskladneni`). When another tab / user makes a
 * mutation, the API publishes a notification and any connected client refreshes
 * the affected TanStack Query cache.
 *
 * Architecture:
 *   1. `useRealtimeConfig` fetches `/v1/realtime/config` once per session — the
 *      response carries the public `subscribe_key` (if PubNub is configured)
 *      and the authenticated user's id (used as the PubNub `uuid` so we can
 *      filter our own publishes).
 *   2. The PubNub SDK (~67 KB gzip) is **lazy-loaded** via `import('pubnub')`
 *      the first time a `useChannel` consumer mounts. Pages that never
 *      subscribe (e.g. Vozidla list) don't pay for the SDK at all.
 *   3. A **module-level singleton** lazily instantiates a single PubNub client
 *      per page load. Every `useChannel` consumer subscribes through that one
 *      instance — multiple subscribers on the same channel share the underlying
 *      socket.
 *   4. `useChannel(channel, onMessage)` registers a listener for `channel` and
 *      calls `onMessage` whenever a remote (other-user) message arrives.
 *      The hook returns nothing — wire it up next to a `useQueryClient()` so
 *      the listener can `invalidateQueries(...)` the relevant cache slice.
 *
 * Graceful degradation: when `subscribe_key` is `null` (e.g., PubNub keys not
 * set in the env), the hook is a no-op — clients pick up changes on their next
 * page-level refetch.
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';

interface RealtimeConfig {
  subscribe_key: string | null;
  user_id: number;
}

// Cache the config for 1 hour — the subscribe key + user id don't change
// across a session, and re-fetching every mount is wasteful.
function useRealtimeConfig() {
  return useQuery({
    queryKey: ['realtime', 'config'],
    queryFn: ({ signal }) =>
      api.get<RealtimeConfig>('/v1/realtime/config', { signal }),
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: false,
  });
}

interface PubNubMessageEvent {
  channel: string;
  publisher?: string;
}

interface PubNubListener {
  message?: (msg: PubNubMessageEvent) => void;
}

interface PubNubClient {
  addListener: (listener: PubNubListener) => void;
  removeListener: (listener: PubNubListener) => void;
  subscribe: (params: { channels: string[] }) => void;
  unsubscribeAll: () => void;
  removeAllListeners: () => void;
}

let clientPromise: Promise<PubNubClient | null> | null = null;
let clientUserId: string | null = null;

async function getClient(
  subscribeKey: string,
  userId: string,
): Promise<PubNubClient | null> {
  if (clientPromise && clientUserId === userId) return clientPromise;
  clientUserId = userId;
  clientPromise = (async () => {
    try {
      const mod = await import('pubnub');
      const PubNub = mod.default;
      return new PubNub({ subscribeKey, userId }) as unknown as PubNubClient;
    } catch (err) {
      console.error('PubNub init failed:', err);
      return null;
    }
  })();
  return clientPromise;
}

/**
 * Subscribe to a PubNub channel and run `onMessage` for every *remote* message.
 * Local publishes (matching the authenticated user's id) are filtered out.
 *
 * The `onMessage` callback is captured in a ref under the hood, so consumers
 * can pass an inline arrow without re-subscribing on every render — but if
 * `onMessage` actually changes identity, the effect re-runs to wire the new
 * listener.
 */
export function useChannel(channel: string, onMessage: () => void) {
  const config = useRealtimeConfig().data ?? null;

  useEffect(() => {
    if (!config?.subscribe_key) return;

    const userId = String(config.user_id);
    let listener: PubNubListener | null = null;
    let client: PubNubClient | null = null;
    let cancelled = false;

    void getClient(config.subscribe_key, userId).then((c) => {
      if (cancelled || !c) return;
      client = c;
      listener = {
        message: (msg) => {
          if (msg.channel !== channel) return;
          if (String(msg.publisher) === userId) return;
          onMessage();
        },
      };
      client.addListener(listener);
      client.subscribe({ channels: [channel] });
    });

    return () => {
      cancelled = true;
      if (client && listener) {
        client.removeListener(listener);
        // Intentionally do NOT unsubscribe — multiple components may share a
        // channel. Leaving the channel attached for the page lifetime is cheap
        // and avoids races where the last unmount drops a channel that
        // another component is about to mount onto.
      }
    };
  }, [channel, config, onMessage]);
}
